import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../lib/redux/hooks";
import { addBannerWithTimeout } from "../../../lib/centralizedBanner/slice";
import { useFindAllQuery } from "../../../../features/usersApi";
import { Layout } from "../../../ui/layout";
import Button from "../../../ui/molecules/button";
import { Spinner } from "../../../ui/molecules/spinner";
import { MenuItems } from "../../../ui/organisms/navbar/dto";
import { NotFound } from "../../../ui/organisms/notFound";
import { FindAllUsersFilters, UserDTO } from "../dto";
import { UsersFilters } from "./filters";
import { UsersList } from "./list";
import { setCreateFromProject } from "../slice";

export function Users() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [filters, setFilters] = useState<FindAllUsersFilters>({ search: "", page: 0, itemsPerPage: 25, order: true });
  const { data, isLoading } = useFindAllQuery(filters);

  const handleDeleteSuccess = (id: string) => {
    dispatch(addBannerWithTimeout({
      message: "Utente eliminato con successo",
      variant: "success"
    }));
  };

  const handleDeleteError = (error: any, id: string) => {
    let errorMessage = "Si è verificato un errore durante l'eliminazione";

    if (error?.status === 409) {
      errorMessage = "Impossibile eliminare: utente associato ad altri dati";
    } else if (error?.status === 403) {
      errorMessage = "Non hai i permessi per eliminare questo utente";
    } else if (error?.status === 404) {
      errorMessage = "Utente non trovato";
    }

    dispatch(addBannerWithTimeout({
      message: errorMessage,
      variant: "error"
    }));
  };

  const handleSort = (field: string) => {
    const validSortFields: (keyof UserDTO)[] = ['name', 'surname', 'email', 'phone', 'role'];
    const sortField = validSortFields.includes(field as keyof UserDTO)
      ? field as keyof UserDTO
      : 'name';

    setFilters(prev => ({
      ...prev,
      sort: sortField,
      order: prev.sort === sortField ? !prev.order : true
    }));
  };

  return (
    <Layout
      menuItem={MenuItems.USERS}
      breadcrumbItems={["Utenti"]}
      headerLabel="Utenti"
      headerChildren={
        <div className="flex items-center h-full w-full justify-end">
          <Button
            size="sm"
            label="Aggiungi utente"
            variant="solid"
            color="blue"
            onClick={() => {
              navigate("/new-user");
              dispatch(setCreateFromProject(undefined));
            }}
            iconPosition={"off"}
          />
        </div>
      }
    >
      <div className="p-4 h-full max-h-full">
        <div className="flex flex-col h-full max-h-full bg-white rounded-xl shadow-sm">
          <UsersFilters filters={filters} onChange={f => setFilters(f)} />
          {isLoading ? (
            <div className="w-full h-[50%] flex items-center justify-center">
              <Spinner />
            </div>
          ) : !data || data.data.length === 0 ? (
            <div style={{ height: "calc(100% - 64px - 60px)" }}>
              <NotFound />
            </div>
          ) : (
            <UsersList
              users={data.data}
              total={data.total}
              page={filters.page}
              itemsPerPage={filters.itemsPerPage}
              sortField={filters.sort}
              sortDirection={filters.order ? 'asc' : 'desc'}
              onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
              onItemsPerPageChange={(itemsPerPage) => setFilters((f) => ({ ...f, itemsPerPage, page: 0 }))}
              onSort={handleSort}
              onEdit={(id) => navigate(`/edit-user/${id}`)}
              onDeleteSuccess={handleDeleteSuccess}
              onDeleteError={handleDeleteError}
            />
          )}
        </div>
      </div>
    </Layout>
  );
}
